import { yupResolver } from '@hookform/resolvers/yup';
import { sendGTMEvent } from '@next/third-parties/google';
import classNames from 'classnames';
import { motion } from 'framer-motion';
import { useRouter } from 'next/router';
import { useTranslation } from 'next-i18next';
import { useCallback, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import EyeIcon from '@/assets/svg/eye-icon';
import type { CreateUserTrialParams } from '@/context/types';
import { useAuth } from '@/hooks/use-auth';
import { apiErrorMessageKey, TRIAL_STATUS_KEYS } from '@/utils/api-error-i18n';
import { personNameSchema } from '@/utils/person-name';
import { getUtmData } from '@/utils/user-tracking-data';

import { AuthLoadingOverlay, SsoProviders } from '../modals/sso';
import type { AlertStateOrEmpty } from '../ui/alert';
import { Alert } from '../ui/alert';
import CaptchaProvider from '../ui/captcha-provider';
import { AlreadyLoggedInNotice } from './already-logged-in-notice';

const defaultValues = {
  name: '',
  email: '',
  repeatEmail: '',
  password: '',
  passwordRepeat: '',
  recaptcha: '',
};

interface FormData {
  name?: string;
  email: string;
  repeatEmail: string;
  password: string;
  passwordRepeat: string;
  recaptcha: string;
}

interface CreateAccountV2Props {
  customTrialData?: {
    custom: number | null;
    trialPageId: number | null;
  };
  daysTrial?: number;
  isDisabledButton?: boolean;
  formId?: string;
  submitLabel?: string;
  accentColor?: string;
  showSocialButtons?: boolean;
}

const NEUTRAL_BORDER = '#1F2730';
const ACCENT_BLUE = '#2ECDFF';
const NOPING_GREEN = '#93FF18';
const DARK_BG = '#080809';
const TEXT_LIGHT = '#E5E7EB';

export default function CreateAccountV2({ customTrialData, daysTrial = 7, isDisabledButton = false, formId = 'reg-form-v2', submitLabel, accentColor = NOPING_GREEN, showSocialButtons = true }: CreateAccountV2Props) {
  const router = useRouter();
  const auth = useAuth();
  const { t } = useTranslation(['components/trial/create-account', 'common']);
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const captchaResetRef = useRef<(() => void) | null>(null);
  const resetCaptcha = useCallback(() => captchaResetRef.current?.(), []);
  const [popUp, setPopUp] = useState<{ show: boolean; message: string; state: AlertStateOrEmpty }>({ show: false, message: '', state: '' });

  const [isSsoAuthenticating, setIsSsoAuthenticating] = useState(false);
  const handleSsoStart = useCallback(() => {
    setIsSsoAuthenticating(true);
  }, []);
  const handleSsoSuccess = useCallback(() => {
    setIsSsoAuthenticating(false);
  }, []);
  const handleSsoError = useCallback((message: string) => {
    setPopUp({ show: true, message, state: 'error' });
    setIsSsoAuthenticating(false);
  }, []);

  const schema = yup.object().shape({
    name: personNameSchema(t('common:login.mensage_error_empty_name'), t('common:login.mensage_error_invalid_name')),
    email: yup.string().email(t('common:login.mensage_error_valid_email')).required(t('common:login.mensage_error_empty_email')).max(255),
    repeatEmail: yup
      .string()
      .email(t('common:login.mensage_error_valid_email'))
      .required(t('common:login.mensage_error_empty_email'))
      .max(255)
      .oneOf([yup.ref('email')], t('common:login.mensage_error_repeat_email')),
    password: yup.string().required(t('common:login.mensage_error_empty_password')).min(6, t('common:login.mensage_error_short_password')),
    passwordRepeat: yup
      .string()
      .required(t('common:login.mensage_error_empty_password'))
      .min(6, t('common:login.mensage_error_short_password'))
      .oneOf([yup.ref('password')], t('common:login.mensage_error_repeat_password')),
    recaptcha: yup.string().required(t('common:login.mensage_error_empty_captch')),
  });

  const {
    register,
    setError,
    handleSubmit,
    setValue,
    clearErrors,
    reset,
    formState: { errors },
  } = useForm({ resolver: yupResolver(schema), defaultValues, mode: 'onBlur' });

  const handleRecaptchaChange = useCallback(
    (token: string | null) => {
      setValue('recaptcha', token ?? '');
      clearErrors('recaptcha');
    },
    [setValue, clearErrors]
  );

  const handleRecaptchaExpired = useCallback(() => {
    setValue('recaptcha', '');
    setError('recaptcha', { type: 'required' });
  }, [setValue, setError]);

  const handleRecaptchaError = useCallback(() => {
    setValue('recaptcha', '');
    resetCaptcha();
  }, [setValue, resetCaptcha]);

  const onSubmit = async (data: FormData) => {
    const { email, password, passwordRepeat, recaptcha, name } = data;
    setIsLoading(true);
    const utmData = getUtmData();

    try {
      const body: CreateUserTrialParams = {
        email,
        password,
        password_repeat: passwordRepeat,
        recaptcha,
        ref: null,
        name,
        ...utmData,
      };
      // `custom` IS the trial config id — the endpoint's name for it. Both ids have to reach the API
      // or the account is created with no trial at all, and it still answers 201.
      if (customTrialData) {
        body.custom = customTrialData.custom;
        body.trialPageId = customTrialData.trialPageId;
      }

      await auth.createTrial(
        { ...body },
        (error) => {
          setValue('recaptcha', '');
          resetCaptcha();
          setPopUp({ show: true, message: t(apiErrorMessageKey(error, { fallbackKey: 'common:login.mensage_error_create_user_generic', statusKeys: TRIAL_STATUS_KEYS })), state: 'error' });
          setIsLoading(false);
        },
        () => {
          auth.login({ login: email, password, recaptcha });
          sendGTMEvent({ event: 'createAccount' });
          reset();
          if (customTrialData) {
            sessionStorage.setItem('customTrialApproved', 'true');
            router.push('/thanks-for-trial-no-card');
          }
          setIsLoading(false);
        },
        false
      );
    } catch {
      setValue('recaptcha', '');
      resetCaptcha();
      setPopUp({ show: true, message: t('common:login.mensage_error_create_user_generic'), state: 'error' });
      setIsLoading(false);
    }
  };

  const inputClass = 'w-full rounded-lg border px-4 py-2.5 text-sm outline-none transition-all focus:ring-2';
  const inputStyle = { backgroundColor: `${ACCENT_BLUE}08`, borderColor: NEUTRAL_BORDER, color: TEXT_LIGHT, fontFamily: 'var(--font-outfit)' };

  // A logged-in visitor on a custom-trial LP should not see a "create a new account" form.
  // CreateAccountV2 is only used by custom-trial LPs. AlreadyLoggedInNotice checks eligibility
  // (SCRUM-1663) and offers to start the SAME trial from the existing account when eligible.
  if (auth.user) {
    return (
      <div className='mx-auto w-full max-w-xl'>
        <AlreadyLoggedInNotice trialConfigId={customTrialData?.custom} trialPageId={customTrialData?.trialPageId} />
      </div>
    );
  }

  return (
    <div className='relative mx-auto w-full max-w-xl'>
      <AuthLoadingOverlay isActive={isSsoAuthenticating} />
      <form id={formId} onSubmit={handleSubmit(onSubmit)} className='flex w-full flex-col gap-2.5' autoComplete='off'>
        {popUp.show && <Alert message={popUp.message} state={popUp.state} onClose={() => setPopUp({ ...popUp, show: false })} />}

        {showSocialButtons && (
          <div className='pb-2'>
            <SsoProviders onAuthStart={handleSsoStart} onAuthSuccess={handleSsoSuccess} onAuthError={handleSsoError} trialContext={customTrialData ? { custom: customTrialData.custom, trialPageId: customTrialData.trialPageId } : undefined} />
          </div>
        )}

        <div>
          <input data-lpignore='true' placeholder={t('trial.input_name')} type='text' {...register('name')} dir='auto' autoComplete='name' className={classNames(inputClass, errors.name && '!border-red-500')} style={inputStyle} />
          {errors.name && <span className='mt-1 block text-xs text-red-500'>{errors.name.message}</span>}
        </div>

        <div>
          <input data-lpignore='true' placeholder={t('trial.input_email')} type='email' {...register('email')} className={classNames(inputClass, errors.email && '!border-red-500')} style={inputStyle} />
          {errors.email && <span className='mt-1 block text-xs text-red-500'>{errors.email.message}</span>}
        </div>

        <div>
          <input data-lpignore='true' placeholder={t('trial.input_repeat_email')} type='email' {...register('repeatEmail')} className={classNames(inputClass, errors.repeatEmail && '!border-red-500')} style={inputStyle} />
          {errors.repeatEmail && <span className='mt-1 block text-xs text-red-500'>{errors.repeatEmail.message}</span>}
        </div>

        <div>
          <div className='relative'>
            <input data-lpignore='true' placeholder={t('trial.input_password')} type={showPassword ? 'text' : 'password'} {...register('password')} className={classNames(inputClass, 'pe-10', errors.password && '!border-red-500')} style={inputStyle} />
            <button type='button' onClick={() => setShowPassword((prev) => !prev)} className='absolute end-3 top-1/2 -translate-y-1/2 text-[#515F6C] hover:opacity-80' aria-label='toggle password'>
              <EyeIcon />
            </button>
          </div>
          {errors.password && <span className='mt-1 block text-xs text-red-500'>{errors.password.message}</span>}
        </div>

        <div>
          <div className='relative'>
            <input data-lpignore='true' placeholder={t('trial.input_confirm_password')} type={showPassword ? 'text' : 'password'} {...register('passwordRepeat')} className={classNames(inputClass, 'pe-10', errors.passwordRepeat && '!border-red-500')} style={inputStyle} />
            <button type='button' onClick={() => setShowPassword((prev) => !prev)} className='absolute end-3 top-1/2 -translate-y-1/2 text-[#515F6C] hover:opacity-80' aria-label='toggle password'>
              <EyeIcon />
            </button>
          </div>
          {errors.passwordRepeat && <span className='mt-1 block text-xs text-red-500'>{errors.passwordRepeat.message}</span>}
        </div>

        <div className='flex items-center justify-center py-1'>
          <CaptchaProvider onChange={handleRecaptchaChange} onExpired={handleRecaptchaExpired} onError={handleRecaptchaError} resetRef={captchaResetRef} />
        </div>
        {errors.recaptcha && <span className='block text-center text-xs text-red-500'>{errors.recaptcha.message}</span>}

        <motion.button type='submit' disabled={isDisabledButton || isLoading} className='w-full cursor-pointer rounded-lg border-none py-3.5 text-sm font-bold disabled:cursor-not-allowed disabled:opacity-60' style={{ background: accentColor, color: DARK_BG, fontFamily: 'var(--font-bai)', boxShadow: `0 0 20px ${accentColor}40` }} whileHover={!isDisabledButton && !isLoading ? { scale: 1.02, boxShadow: `0 0 30px ${accentColor}60` } : undefined} whileTap={!isDisabledButton && !isLoading ? { scale: 0.98 } : undefined}>
          {isLoading ? t('trial.buttons.submitting') : (submitLabel ?? t('trial.buttons.sign_up', { count: daysTrial }))}
        </motion.button>
      </form>
    </div>
  );
}

export { NOPING_GREEN };
